import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent, DialogTitle, Box, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { FormTextField } from '../../../component/form/FormTextField';
import { EnhancedDialog } from '../../../component/EnhancedDialog';
import {
  CreateFeatureDefinitionRequest,
  FeatureDefinitionView,
  FeatureType,
  UpdateFeatureDefinitionRequest,
} from '../../../generated/ApiClient';
import { FormDropdown } from '../../../component/form/FormDropdown';
import RestHttpClient from '../../../common/RestHttpClient';
import { useFeatureTypes } from './useFeatureTypes';

interface FeatureDialogProps {
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  feature: FeatureDefinitionView | null;
  onClose: () => void;
}

const FeatureDialog = ({ isOpen, setOpen, feature, onClose }: FeatureDialogProps) => {
  const { t } = useTranslation();

  const isEdit = !!feature;

  const schema = z.object({
    name: z.string().min(1),
    featureType: z.nativeEnum(FeatureType),
    format: z.string().optional(),
  });

  type FormValues = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  const { featureTypes } = useFeatureTypes();

  useEffect(() => {
    if (feature) reset(feature);
  }, [feature, reset]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const onFormSubmit = async (data: FormValues) => {
    try {
      if (isEdit && feature) {
        await RestHttpClient.updateFeatureDefinition(
          feature.id,
          new UpdateFeatureDefinitionRequest({
            id: feature.id,
            name: data.name,
            featureType: data.featureType,
            format: data.format || '',
          })
        );
      } else {
        await RestHttpClient.createFeatureDefinition(
          new CreateFeatureDefinitionRequest({
            name: data.name,
            featureType: data.featureType,
            format: data.format || '',
          })
        );
      }
      handleClose();
    } catch (error) {
      console.error('Error saving feature:', error);
    }
  };

  return (
    <EnhancedDialog isDialogOpened={isOpen} setIsDialogOpened={setOpen}>
      <DialogTitle>
        {isEdit
          ? t('classification.features.editFeature')
          : t('classification.features.addFeature')}
      </DialogTitle>
      <DialogContent dividers>
        <form id="feature-form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <Grid container spacing={3} sx={{ paddingY: 2 }}>
            <Grid item xs={12}>
              <FormTextField
                control={control}
                name="name"
                label={t('classification.features.name')}
                disabled={isSubmitting}
                shrink
              />
            </Grid>
            <Grid item xs={12}>
              <FormDropdown
                control={control}
                name="featureType"
                label={t('classification.features.type')}
                disabled={isSubmitting}
                options={featureTypes}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                control={control}
                name="format"
                label={t('classification.features.format')}
                disabled={isSubmitting}
                shrink
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Box sx={{ textAlign: 'right' }}>
          <Button
            onClick={handleClose}
            sx={{ border: 1, borderColor: 'black', color: 'black', marginRight: '4px' }}
            disabled={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" form="feature-form" variant="contained" disabled={isSubmitting}>
            {t('save')}
          </Button>
        </Box>
      </DialogActions>
    </EnhancedDialog>
  );
};

export default FeatureDialog;
